import { MultiSelect, Button } from '@atrvd/saas-ui'
import { X } from 'lucide-react'
import type { CashPositionFilterState } from './cashPositionColumns'

/**
 * Props for the Cash Position Summary filter bar (DCP-145).
 */
interface CashPositionFiltersProps {
  /** Current filter selection. */
  filters: CashPositionFilterState
  /** Called with the next selection whenever a filter changes. */
  onFiltersChange: (filters: CashPositionFilterState) => void
  /** Pack options ({ value: id, label: name }), name-ASC. */
  packOptions: Array<{ value: string; label: string }>
  /** Facility options ({ value: id, label: name }), name-ASC. */
  facilityOptions: Array<{ value: string; label: string }>
}

/**
 * Filter bar for the Cash Position Summary grid (DCP-145) — replaces the mockup's
 * per-pack/per-facility tabs with two multi-select pickers. Empty selection on
 * both means "All" (every pack expanded + every standalone facility).
 *
 * DCP-306: rendered borderless and inline so it can live in the page-header's
 * top-right `actions` slot (compact, single row) instead of a separate boxed
 * filter bar — freeing vertical space so the report fits one screen.
 */
export function CashPositionFilters({
  filters,
  onFiltersChange,
  packOptions,
  facilityOptions,
}: CashPositionFiltersProps) {
  // Any active filter enables the clear affordance.
  const hasActiveFilters = filters.packIds.length > 0 || filters.facilityIds.length > 0

  return (
    // Bottom-aligned so the two labelled selects and the clear button share a
    // baseline; no outer box/background — the page header provides the framing.
    <div className="flex flex-wrap items-end gap-3">
      {/* Pack Filter — each selected pack renders expanded (members + subtotal). */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Packs</span>
        <MultiSelect
          options={packOptions}
          value={filters.packIds}
          onChange={(value) => onFiltersChange({ ...filters, packIds: value })}
          placeholder="All"
          className="w-[200px]"
          searchable
          searchPlaceholder="Search packs..."
        />
      </div>

      {/* Facility Filter — each selected facility renders as its own column. */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Facilities</span>
        <MultiSelect
          options={facilityOptions}
          value={filters.facilityIds}
          onChange={(value) => onFiltersChange({ ...filters, facilityIds: value })}
          placeholder="All"
          className="w-[200px]"
          searchable
          searchPlaceholder="Search facilities..."
        />
      </div>

      {/* Clear Filters — only shown when something is selected. */}
      {hasActiveFilters && (
        <Button
          onClick={() => onFiltersChange({ packIds: [], facilityIds: [] })}
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          title="Clear filters"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
